// frontend/src/pages/SettingsPage.jsx
// User settings — profile update, password change, account deletion.

import { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, DarkModeContext } from '../App';
import {
  logout as apiLogout,
  updateProfile,
  changePassword,
  deleteAccount
} from '../utils/api';

export default function SettingsPage() {
  const { user, logout, setUser } = useContext(AuthContext);
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const handleLogout = async () => {
    try { await apiLogout(); } catch (e) { /* ignore */ }
    logout();
    navigate('/');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!displayName.trim()) { setError('Display name cannot be empty'); return; }
    try {
      setBusy(true);
      const updated = await updateProfile({ displayName: displayName.trim() });
      if (setUser) setUser({ ...user, displayName: updated.displayName });
      setSuccess('Profile updated.');
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    try {
      setBusy(true);
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setSuccess('Password changed.');
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const handleDeleteAccount = async () => {
    setError(''); setSuccess('');
    if (!deletePassword) { setError('Enter your password to confirm deletion'); return; }
    if (!deleteConfirm) { setError('Tick the confirmation box first'); return; }
    try {
      setBusy(true);
      await deleteAccount({ password: deletePassword });
      logout();
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-gray-100">Patent Council</Link>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">{user?.displayName}</span>
              <Link to="/dashboard" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">Dashboard</Link>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
                aria-label="Toggle dark mode"
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button onClick={handleLogout} className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Settings</h1>

        {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4" role="alert">{error}</div>}
        {success && <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded mb-4">{success}</div>}

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Profile</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className={`${inputCls} bg-gray-100 dark:bg-gray-900`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Email cannot be changed.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Name</label>
              <input
                type="text"
                maxLength={100}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={inputCls}
              />
            </div>
            <button type="submit" disabled={busy} className="bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
              {busy ? 'Saving...' : 'Update Profile'}
            </button>
          </form>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Change Password</h2>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password (min 8 chars)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>
            <button type="submit" disabled={busy} className="bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
              {busy ? 'Saving...' : 'Change Password'}
            </button>
          </form>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-2 border-red-200 dark:border-red-800">
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Deleting your account permanently removes your profile, all projects, all seed documents, all rounds and messages. This cannot be undone.
          </p>
          <div className="space-y-3">
            <input
              type="password"
              placeholder="Enter your password to confirm"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className={inputCls}
            />
            <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.checked)} className="mr-2" />
              I understand this is permanent.
            </label>
            <button
              onClick={handleDeleteAccount}
              disabled={busy || !deletePassword || !deleteConfirm}
              className="bg-red-600 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm hover:bg-red-700"
            >
              {busy ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
